import { Link } from 'react-router-dom'
import './styles/Landing.css'
// Barlow ist die Hausschrift von marcapo (siehe ui/theme.css); diese Seite ist
// marcapo-gebrandet, nicht markenneutral.
import '@fontsource/barlow/400.css'
import '@fontsource/barlow/600.css'
import '@fontsource/barlow/700.css'

const DEMO_URL = 'https://www.marcapo.com/kontakt-demotermin'
const MARCAPO_URL = 'https://www.marcapo.com'
const IMPRESSUM_URL = 'https://www.marcapo.com/impressum'
const DATENSCHUTZ_URL = 'https://www.marcapo.com/datenschutz'

/** Externer Link — immer neues Fenster, nie Zugriff auf unser `window`. */
function Extern({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) {
  return (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

type Kachel = { titel: string; text: string; icon: React.ReactNode }

const STRICH = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const

const IconMarke = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...STRICH}>
    <path d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
)
const IconWege = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...STRICH}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <path d="M14 14h3v3h-3zM20 20h1M18 20v1" />
  </svg>
)
const IconAktuell = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...STRICH}>
    <path d="M20 12a8 8 0 1 1-2.3-5.6" />
    <path d="M20 4v4h-4" />
    <path d="M12 8v4l2.5 2" />
  </svg>
)
const IconZahlen = (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...STRICH}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
)

const KACHELN: Kachel[] = [
  {
    titel: 'Markenkonform ohne Nachfassen',
    text: 'Logo, Farben, Schrift und Aufbau kommen aus der Vorlage der Marke. Wer sein Profil pflegt, füllt Felder aus — am Erscheinungsbild kann er nichts verstellen.',
    icon: IconMarke,
  },
  {
    titel: 'Ein Profil, drei Wege',
    text: 'Link, QR-Code und NFC-Karte führen auf dieselbe Karte. Wer sie öffnet, speichert den Kontakt mit einem Tipp im Telefon — ohne App, ohne Registrierung.',
    icon: IconWege,
  },
  {
    titel: 'Immer aktuell, auch gedruckt',
    text: 'Eine neue Durchwahl ist eine Änderung im Profil, kein Nachdruck. Gedruckte QR-Codes und ausgegebene Karten bleiben gültig.',
    icon: IconAktuell,
  },
  {
    titel: 'Zahlen ohne Einwilligungsbanner',
    text: 'Gezählt werden Aufrufe und Klicks je Profil — keine IP-Adresse, kein Cookie, nichts im Endgerät. Deshalb braucht die Karte kein Banner.',
    icon: IconZahlen,
  },
]

type Schritt = { titel: string; text: string }

const SCHRITTE: Schritt[] = [
  {
    titel: 'Marke einrichten',
    text: 'Logo, Farben und Vorlage einmal hinterlegen. Ab da gilt das Erscheinungsbild für jedes Profil dieser Marke.',
  },
  {
    titel: 'Profile anlegen',
    text: 'Einzeln in der Konsole oder automatisch: die REST-Schnittstelle legt Profile aus dem System der Marke an und hält sie aktuell.',
  },
  {
    titel: 'Daten selbst pflegen',
    text: 'Jeder pflegt sein eigenes Profil — Anmeldung per Link in der E-Mail, kein Passwort. Die Marke sieht und verwaltet alle Profile.',
  },
  {
    titel: 'Verteilen',
    text: 'Link in die E-Mail-Signatur, QR-Code auf die gedruckte Karte oder den Aufsteller, NFC-Karte in die Tasche.',
  },
  {
    titel: 'Wirkung sehen',
    text: 'Die Konsole zeigt Aufrufe und Klicks je Profil und je Marke — welcher Kontaktweg tatsächlich benutzt wird, steht dort schwarz auf weiß.',
  },
]

type Kanal = { titel: string; text: string }

const KANAELE: Kanal[] = [
  {
    titel: 'QR-Code',
    text: 'Auf der gedruckten Visitenkarte, dem Aufsteller im Schaufenster, dem Messestand. Kamera drauf, Karte offen.',
  },
  {
    titel: 'NFC-Karte',
    text: 'Karte ans Telefon halten, fertig. Kein Scannen, kein Tippen — der Weg, der im Gespräch am wenigsten stört.',
  },
  {
    titel: 'Link',
    text: 'In der E-Mail-Signatur, im Messenger, auf der Partnerseite. Eine Adresse je Person, dauerhaft gültig.',
  },
]

type Punkt = { titel: string; text: string }

const DATENSCHUTZ: Punkt[] = [
  {
    titel: 'Kein Banner, weil nichts gespeichert wird',
    text: 'Die Karte setzt kein Cookie und legt nichts im Endgerät ab. Gezählt wird je Profil und Tag, ohne Wiedererkennung — deshalb heißt die Zahl „Aufrufe" und nicht „Besucher".',
  },
  {
    titel: 'Drei Stufen beim Ausscheiden',
    text: 'Deaktivieren nimmt die Karte aus dem Netz und lässt die Daten. Der Papierkorb ist umkehrbar und läuft nach 30 Tagen ab. Anonymisieren ist endgültig.',
  },
  {
    titel: 'Alte QR-Codes führen nie auf eine fremde Person',
    text: 'Die Adresse einer gelöschten Karte wird gesperrt statt neu vergeben. Ein Code aus dem letzten Katalog läuft ins Leere — nicht auf den Nachfolger.',
  },
]

function Landing() {
  // T-04 (Spec §2.4): Nunito Sans wird bedingt nachgeladen statt global
  // (index.tsx) — nur der Classic-Rückfall (ClassicTemplate) und diese
  return (
    <div className="lp">
      <header className="lp-kopf">
        <div className="lp-breite lp-kopf-inhalt">
          <img src="/vcard-logo.svg" alt="vcard — digitale Visitenkarten von marcapo" className="lp-logo" width={122} height={40} />
          <nav className="lp-kopf-nav">
            <Link to="/admin/login" className="lp-link-schlicht">
              Anmelden
            </Link>
            <Extern href={DEMO_URL} className="lp-knopf lp-knopf-klein">
              Demo-Termin vereinbaren
            </Extern>
          </nav>
        </div>
      </header>

      <main>
        {/* --- Hero ------------------------------------------------------- */}
        <section className="lp-hero">
          <div className="lp-breite lp-hero-raster">
            <div className="lp-hero-text">
              <p className="lp-augenbraue">vcard</p>
              <h1>Die digitale Visitenkarte deiner Marke — vor Ort, in der Hand des Kunden</h1>
              <p className="lp-lead">
                Jeder Mitarbeiter, jeder Partner vor Ort bekommt ein eigenes Profil unter einer festen Adresse:
                Foto, Funktion, Durchwahl, Standort, Öffnungszeiten. Aufgerufen per QR-Code, NFC-Karte oder Link,
                gespeichert mit einem Tipp im Telefon des Gegenübers. Das Erscheinungsbild gibt die Marke vor —
                gepflegt wird dezentral.
              </p>
              <div className="lp-knopfreihe">
                <Extern href={DEMO_URL} className="lp-knopf">
                  Jetzt Demo-Termin vereinbaren
                </Extern>
                <a href="#ablauf" className="lp-link-schlicht lp-link-pfeil">
                  So läuft es ab
                </a>
              </div>
            </div>

            <div className="lp-hero-bilder">
              <img
                className="lp-bild-telefon"
                src="/vcard.png"
                alt="Digitale Visitenkarte auf einem Telefon: Logo, Portraitfoto, Name, Funktion und die Schaltflächen Kontakt speichern und Termin vereinbaren"
                loading="eager"
              />
              <img
                className="lp-bild-nfc"
                src="/nfc.png"
                alt="NFC-Karte im Markendesign mit aufgedrucktem QR-Code"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* --- Vier Kacheln ----------------------------------------------- */}
        <section className="lp-abschnitt">
          <div className="lp-breite">
            <h2>Eine Karte, die die Marke trägt</h2>
            <p className="lp-einleitung">
              Gedruckte Visitenkarten veralten in dem Moment, in dem sich eine Durchwahl ändert — und jede
              Nachbestellung ist eine neue Gelegenheit, vom Erscheinungsbild abzuweichen. vcard dreht beides um:
              die Marke legt die Vorlage fest, die Daten pflegt der, dem sie gehören, und die Karte ist in dem
              Augenblick aktuell, in dem sie gespeichert wird.
            </p>

            <ul className="lp-kacheln">
              {KACHELN.map((k) => (
                <li key={k.titel} className="lp-kachel">
                  <span className="lp-kachel-icon" aria-hidden="true">
                    {k.icon}
                  </span>
                  <h3>{k.titel}</h3>
                  <p>{k.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* --- Fünf Schritte ---------------------------------------------- */}
        <section className="lp-abschnitt lp-abschnitt-hell" id="ablauf">
          <div className="lp-breite">
            <h2 id="ablauf-titel">Fünf Schritte bis zur ersten Karte</h2>
            <p className="lp-einleitung">
              Der Aufwand liegt vorn: Marke einrichten, Anbindung klären. Danach entsteht ein Profil so nebenbei
              wie ein Eintrag im Telefonbuch.
            </p>

            <ol className="lp-schritte" aria-labelledby="ablauf-titel">
              {SCHRITTE.map((s, i) => (
                <li key={s.titel} className="lp-schritt">
                  <span className="lp-schritt-nummer" data-testid="schritt-nummer" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <h3>{s.titel}</h3>
                    <p>{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* --- Kanäle ------------------------------------------------------ */}
        <section className="lp-abschnitt">
          <div className="lp-breite">
            <h2>Drei Wege, ein Profil</h2>
            <p className="lp-einleitung">
              Welcher Weg im Gespräch passt, entscheidet die Situation — die Karte dahinter ist immer dieselbe.
              Wer sie geöffnet hat, speichert den Kontakt als echten Telefonbucheintrag, vereinbart einen Termin
              oder startet Anruf, WhatsApp oder E-Mail direkt aus der Karte heraus.
            </p>

            <ul className="lp-kanaele">
              {KANAELE.map((k) => (
                <li key={k.titel} className="lp-kanal">
                  <h3>{k.titel}</h3>
                  <p>{k.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* --- Datenschutz -------------------------------------------------- */}
        <section className="lp-abschnitt lp-abschnitt-dunkel">
          <div className="lp-breite">
            <h2>Datenschutz ist eingebaut, nicht angebaut</h2>
            <p className="lp-einleitung">
              Eine Visitenkarte besteht aus Personendaten — das ist ihr Zweck. Entsprechend ist der Umgang damit
              Teil des Produkts und nicht eine Einstellung, die jemand vergessen kann.
            </p>

            <ul className="lp-punkte">
              {DATENSCHUTZ.map((p) => (
                <li key={p.titel} className="lp-punkt">
                  <h3>{p.titel}</h3>
                  <p>{p.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* --- Extras ------------------------------------------------------- */}
        <section className="lp-abschnitt">
          <div className="lp-breite">
            <h2>Das Extra für Marken mit eigenem Auftritt</h2>

            <div className="lp-extra">
              <div>
                <h3>Eigenes Template statt Baukasten</h3>
                <p>
                  Wo die Standardkarte nicht reicht, bekommt die Marke ihr eigenes Template: eigenes Layout,
                  eigene Typografie, eigene Bildsprache. Gebaut wird es gegen einen festen Vertrag, der genau die
                  Felder liefert, die eine Karte braucht — und keine Personendaten, die sie nicht braucht.
                </p>
              </div>
              <div>
                <h3>Eigene Domain der Marke</h3>
                <p>
                  Die Karten können unter der Domain der Marke laufen statt unter einer fremden. Die Adresse, die
                  auf dem gedruckten Material steht, gehört dann der Marke — mit allem, was das für Vertrauen
                  und Wiedererkennung bedeutet.
                </p>
              </div>
              <div>
                <h3>Anbindung an das bestehende System</h3>
                <p>
                  Profile entstehen und verschwinden dort, wo die Personaldaten ohnehin gepflegt werden. Die
                  REST-Schnittstelle übernimmt Anlage, Aktualisierung und Deaktivierung; abgesichert per OAuth 2.0,
                  je anbindendes System ein eigener Zugang mit eigener Markenbindung.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* --- Abschluss ---------------------------------------------------- */}
        <section className="lp-abschluss">
          <div className="lp-breite lp-abschluss-inhalt">
            <h2>Mach die Visitenkarte deiner Partner digital — und lass sie trotzdem nach Marke aussehen.</h2>
            <Extern href={DEMO_URL} className="lp-knopf lp-knopf-hell">
              Jetzt Demo-Termin vereinbaren
            </Extern>
          </div>
        </section>
      </main>

      <footer className="lp-fuss">
        <div className="lp-breite lp-fuss-inhalt">
          <img src="/vcard-logo.svg" alt="vcard — digitale Visitenkarten von marcapo" className="lp-logo" width={122} height={40} />
          <nav className="lp-fuss-nav">
            <Extern href={MARCAPO_URL}>marcapo.com</Extern>
            <Extern href={IMPRESSUM_URL}>Impressum</Extern>
            <Extern href={DATENSCHUTZ_URL}>Datenschutz</Extern>
          </nav>
        </div>
      </footer>
    </div>
  )
}

export default Landing
